/**
 * @fileoverview Deferred Media Component
 *
 * A custom element that defers loading of a video (native `<video>` or
 * YouTube/Vimeo iframe) until needed, then manages play/pause/mute state and
 * coordinates with other media on the page so only one interactive video plays
 * at a time.
 *
 * @example
 * <deferred-media autoplay>
 *   <button ref="deferredMediaPlayButton">Play</button>
 *   <template><video class="video__element" src="…"></video></template>
 * </deferred-media>
 */

// Global events for video management
const EVENTS = {
  MEDIA_STARTED: 'theme:media:started',
  MEDIA_PAUSED: 'theme:media:paused'
};

/**
 * @classdesc Lazily loads and controls a video, coordinating playback across the page.
 * @extends HTMLElement
 */
export class DeferredMedia extends HTMLElement {
  #abortController = new AbortController();
  #isPlaying = false;
  #isMuted = false;
  #isLoaded = false;
  #refs = {
    deferredMediaPlayButton: null,
    toggleMediaButton: null,
    toggleSoundButton: null
  };

  // Video service configurations for secure postMessage targeting.
  // Shopify video_url input only accepts YouTube and Vimeo, so we can safely restrict to these origins.
  static videoServices = {
    youtube: {
      urlPattern: 'https://www.youtube.com/embed/',
      origin: 'https://www.youtube.com',
      commands: {
        play: 'playVideo',
        pause: 'pauseVideo',
        mute: 'mute',
        unmute: 'unMute'
      },
      formatMessage: (func, value = '') => JSON.stringify({
        event: 'command',
        func: func,
        args: value
      })
    },
    vimeo: {
      urlPattern: 'https://player.vimeo.com/video/',
      origin: 'https://player.vimeo.com',
      commands: {
        play: 'play',
        pause: 'pause',
        mute: 'setVolume',
        unmute: 'setVolume'
      },
      formatMessage: (method, value) => JSON.stringify(
        value !== undefined ? { method, value } : { method }
      )
    }
  };

  /**
   * Get the video service configuration for a given iframe.
   * @param {HTMLIFrameElement} iframe - The iframe element to check
   * @returns {Object|null} The service config with name and config, or null if not recognized
   */
  #getVideoService(iframe) {
    if (!iframe?.src) return null;

    for (const [name, config] of Object.entries(DeferredMedia.videoServices)) {
      if (iframe.src.startsWith(config.urlPattern)) {
        return { name, config };
      }
    }

    console.warn(`DeferredMedia: Unrecognized video service for iframe: ${iframe.src}`);
    return null;
  }

  /**
   * Send a postMessage to the iframe with the correct origin.
   * @param {HTMLIFrameElement} iframe - The target iframe
   * @param {string} command - The command type (play, pause, mute, unmute)
   * @param {*} value - Optional value for the command
   */
  #sendVideoMessage(iframe, command, value) {
    if (!iframe?.contentWindow) return;

    const service = this.#getVideoService(iframe);
    if (!service) return;

    const func = service.config.commands[command];
    if (!func) {
      console.warn(`DeferredMedia: Unknown command "${command}" for ${service.name}`);
      return;
    }

    const message = service.config.formatMessage(func, value);

    try {
      iframe.contentWindow.postMessage(message, service.config.origin);
    } catch (error) {
      console.error(`DeferredMedia: Error sending message to ${service.name} iframe:`, error);
    }
  }

  connectedCallback() {
    const signal = this.#abortController.signal;

    // Get refs by attribute
    this.#updateRefs();

    // Listen for other media starting (pause this one)
    document.addEventListener(EVENTS.MEDIA_STARTED, this.#handleGlobalMediaStart.bind(this), { signal });

    // Handle play button clicks
    if (this.#refs.deferredMediaPlayButton) {
      this.#refs.deferredMediaPlayButton.addEventListener('click', this.showDeferredMedia.bind(this), { signal });
    }

    // Handle toggle button clicks
    if (this.#refs.toggleMediaButton) {
      this.#refs.toggleMediaButton.addEventListener('click', this.toggleMedia.bind(this), { signal });
    }

    // Handle sound button clicks
    if (this.#refs.toggleSoundButton) {
      this.#refs.toggleSoundButton.addEventListener('click', this.toggleSound.bind(this), { signal });
    }

    // Auto-load if autoplay is set and content not already loaded
    if (this.hasAttribute('autoplay') && !this.hasAttribute('data-media-loaded')) {
      this.#loadContent();
    }
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  muteMedia() {
    const video = this.querySelector('.video__element');
    const iframe = this.querySelector('.video__iframe');

    if (video) {
      video.muted = true;
    } else if (iframe) {
      this.#sendVideoMessage(iframe, 'mute', 0);
    }

    this.#isMuted = true;
    this.updateSoundHint(true);
  }

  unmuteMedia() {
    const video = this.querySelector('.video__element');
    const iframe = this.querySelector('.video__iframe');

    if (video) {
      video.muted = false;
    } else if (iframe) {
      this.#sendVideoMessage(iframe, 'unmute', 1);
    }

    this.#isMuted = false;
    this.updateSoundHint(false);
  }

  #updateRefs() {
    // Find elements by ref attribute
    const deferredButton = this.querySelector('[ref="deferredMediaPlayButton"]');
    const toggleButton = this.querySelector('[ref="toggleMediaButton"]');
    const soundButton = this.querySelector('[ref="toggleSoundButton"]');

    if (deferredButton) this.#refs.deferredMediaPlayButton = deferredButton;
    if (toggleButton) this.#refs.toggleMediaButton = toggleButton;
    if (soundButton) this.#refs.toggleSoundButton = soundButton;
  }

  /**
   * Check if this is a background video (no user controls).
   * Background videos should not be affected by global media events
   * and should not affect other videos when they play.
   * @returns {boolean} True if this is a background video with no controls
   */
  #isBackgroundVideo() {
    return !this.#refs.toggleMediaButton && !this.#refs.toggleSoundButton;
  }

  showDeferredMedia() {
    this.#loadContent();
    this.#playMedia();
  }

  toggleMedia() {
    if (this.#isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  toggleSound() {
    if (this.#isMuted) {
      this.unmuteMedia();
    } else {
      this.muteMedia();
    }
  }

  playMedia() {
    if (!this.#isLoaded) {
      this.#loadContent();
      return;
    }

    const video = this.querySelector('.video__element');
    const iframe = this.querySelector('.video__iframe');

    if (video) {
      video.play().catch(console.warn);
    } else if (iframe) {
      this.#sendVideoMessage(iframe, 'play');
    }

    this.#isPlaying = true;
    this.updatePlayPauseHint(true);
  }

  pauseMedia() {
    const video = this.querySelector('.video__element');
    const iframe = this.querySelector('.video__iframe');

    if (video) {
      video.pause();
    } else if (iframe) {
      this.#sendVideoMessage(iframe, 'pause');
    }

    this.#isPlaying = false;
    this.updatePlayPauseHint(false);
  }

  updatePlayPauseHint(isPlaying) {
    const toggleButton = this.#refs.toggleMediaButton;
    if (!toggleButton) return;

    toggleButton.classList.remove('hidden');

    const playIcon = toggleButton.querySelector('.icon-play');
    const pauseIcon = toggleButton.querySelector('.icon-pause');

    if (playIcon) {
      playIcon.classList.toggle('hidden', isPlaying);
    }
    if (pauseIcon) {
      pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  updateSoundHint(isMuted) {
    const toggleButton = this.#refs.toggleSoundButton;
    if (!toggleButton) return;

    toggleButton.classList.remove('hidden');

    const mutedIcon = toggleButton.querySelector('.icon-muted');
    const unmutedIcon = toggleButton.querySelector('.icon-unmuted');

    if (mutedIcon) {
      mutedIcon.classList.toggle('hidden', !isMuted);
    }
    if (unmutedIcon) {
      unmutedIcon.classList.toggle('hidden', isMuted);
    }
  }

  #handleGlobalMediaStart(event) {
    // Background videos (no controls) should not be affected by global media events
    if (this.#isBackgroundVideo()) {
      return;
    }

    // Pause this video if another one starts
    if (event.detail.source !== this && this.#isPlaying) {
      this.pauseMedia();
    }
  }

  #loadContent() {
    if (this.#isLoaded) return;

    const template = this.querySelector('template');
    if (!template) return;

    // Only dispatch global event for interactive videos (with controls).
    // Background videos should not pause other videos when they autoplay.
    if (!this.#isBackgroundVideo()) {
      document.dispatchEvent(new CustomEvent(EVENTS.MEDIA_STARTED, {
        detail: { source: this }
      }));
    }

    // Clone and append template content
    const content = template.content.cloneNode(true);
    this.appendChild(content);

    // Mark as loaded
    this.#isLoaded = true;
    this.setAttribute('data-media-loaded', 'true');

    // Hide the initial play button (not the toggle button)
    if (this.#refs.deferredMediaPlayButton) {
      this.#refs.deferredMediaPlayButton.classList.add('hidden');
    }

    // Handle video element loading
    const video = this.querySelector('.video__element');
    if (video) {
      this.#setupVideoElement(video);
      // Sync muted state with the video element after setup
      this.#isMuted = video.muted;
      this.updateSoundHint(video.muted);
    }

    // Handle iframe loading
    const iframe = this.querySelector('.video__iframe');
    if (iframe) {
      this.#setupIframeElement(iframe);
    }
  }

  #setupVideoElement(video) {
    // Ensure Safari/iOS autoplay requirements are met
    if (this.hasAttribute('autoplay')) {
      video.muted = true;
      video.setAttribute('muted', '');
    }
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    const onCanPlay = () => {
      video.removeEventListener('canplay', onCanPlay);
      if (video.hasAttribute('autoplay')) {
        video.play().catch(console.warn);
      }
    };

    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('play', () => {
      this.#isPlaying = true;
      this.updatePlayPauseHint(true);
    });
    video.addEventListener('pause', () => {
      this.#isPlaying = false;
      this.updatePlayPauseHint(false);
    });
    video.addEventListener('ended', () => {
      this.#isPlaying = false;
      this.updatePlayPauseHint(false);
    });
  }

  #setupIframeElement(iframe) {
    // For iframes, we assume they start playing immediately if autoplay
    if (this.hasAttribute('autoplay')) {
      this.#isPlaying = true;
      this.updatePlayPauseHint(true);
      // YouTube/Vimeo iframes start muted when autoplay is enabled (browser requirement)
      this.#isMuted = true;
      this.updateSoundHint(true);
    } else {
      // Non-autoplay videos: user clicked to play, so they start with audio enabled
      this.#isMuted = false;
      this.updateSoundHint(false);
    }

    // Listen for messages from YouTube/Vimeo
    // Use abort signal to ensure cleanup when element is disconnected (prevents memory leak)
    window.addEventListener('message', (event) => {
      if (event.source === iframe.contentWindow) {
        this.#handleIframeMessage(event.data);
      }
    }, { signal: this.#abortController.signal });
  }

  #handleIframeMessage(data) {
    try {
      const parsed = typeof data === 'string' ? JSON.parse(data) : data;

      // YouTube events
      if (parsed.event === 'onStateChange') {
        // YouTube player states: -1 (unstarted), 0 (ended), 1 (playing), 2 (paused), 3 (buffering), 5 (cued)
        if (parsed.info === 1) {
          this.#isPlaying = true;
          this.updatePlayPauseHint(true);
        } else if (parsed.info === 2 || parsed.info === 0) {
          this.#isPlaying = false;
          this.updatePlayPauseHint(false);
        }
      }

      // Vimeo events
      if (parsed.event === 'play') {
        this.#isPlaying = true;
        this.updatePlayPauseHint(true);
      } else if (parsed.event === 'pause' || parsed.event === 'ended') {
        this.#isPlaying = false;
        this.updatePlayPauseHint(false);
      }
    } catch (e) {
      // Ignore parsing errors
    }
  }

  #playMedia() {
    if (!this.#isLoaded) {
      this.#loadContent();
      return;
    }

    const video = this.querySelector('.video__element');
    const iframe = this.querySelector('.video__iframe');

    if (video) {
      video.play().catch(console.warn);
    } else if (iframe) {
      this.#sendVideoMessage(iframe, 'play');
    }

    this.#isPlaying = true;
    this.updatePlayPauseHint(true);
  }

  // Public API
  play() {
    this.playMedia();
  }

  pause() {
    this.pauseMedia();
  }

  get isPlaying() {
    return this.#isPlaying;
  }

  get isLoaded() {
    return this.#isLoaded;
  }
}

// Register custom element
if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

// Initialize existing elements (for cases where JS loads after HTML)
const initializeExistingElements = () => {
  document.querySelectorAll('deferred-media:not(:defined)').forEach(element => {
    // Force upgrade if not already upgraded
    customElements.upgrade(element);
  });
};

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeExistingElements);
} else {
  initializeExistingElements();
}

// Handle dynamically added content
document.addEventListener('shopify:section:load', initializeExistingElements);
document.addEventListener('shopify:block:select', initializeExistingElements);
